import { ApolloClient, InMemoryCache, createHttpLink, from } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';
import { getCookie } from '@/utils/cookies';
import { env } from '@/utils/env';
import { formatApiUrl, logApiRequest } from '@/lib/utils';

// Format the GraphQL URL from environment variables
const graphqlUrl = formatApiUrl(env.graphqlUrl || 'http://localhost:3000/graphql');

// Log connection details during development
if (import.meta.env.DEV) {
  console.log('Apollo Client connecting to:', graphqlUrl);
}

// Error handling link
const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      console.error(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
      );
    });
  }
  
  if (networkError) {
    console.error(`[Network error]: ${networkError}`);
  }
});

// HTTP link with request logging in development
const httpLink = createHttpLink({
  uri: graphqlUrl,
  fetchOptions: {
    credentials: 'include', // This helps with cookies if your API uses them
  },
});

// Auth link to add the token to requests
const authLink = setContext((_, { headers }) => {
  // Get the authentication token from cookies
  const token = getCookie('token');
  
  // Log request details in development
  if (import.meta.env.DEV) {
    logApiRequest(
      graphqlUrl, 
      'POST', 
      { ...headers, Authorization: token ? `Bearer ${token}` : '' },
      'GraphQL Operation'
    );
  }
  
  // Return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      Authorization: token ? `Bearer ${token}` : '',
    },
  };
});

// Create the Apollo Client
const apolloClient = new ApolloClient({
  link: from([errorLink, authLink, httpLink]),
  cache: new InMemoryCache(),
  connectToDevTools: import.meta.env.DEV, // Enable Apollo dev tools in development
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'network-only', // Don't use cache by default
      errorPolicy: 'all',
    },
    query: {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    },
  },
});

export default apolloClient;
